import { z } from 'zod';

// Catalog row shape returned to the client. Promoted from the inline
// `CatalogListItem` type in apps/products/api/src/catalogs/service.ts so the
// router can use the same schema as the type source.
export const catalogSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  isExcludedFromCms: z.boolean(),
});
export type Catalog = z.infer<typeof catalogSchema>;

// `catalogs.create` — only `title` is required. `isExcludedFromCms` defaults
// false on the DB side. `organizationId` is derived from ctx.user, never on
// the input.
export const createCatalogInput = z.object({
  title: z.string().min(1).max(255),
  isExcludedFromCms: z.boolean().optional(),
});
export type CreateCatalogInput = z.infer<typeof createCatalogInput>;

// Patch shape — independent optionals with the "at least one field" refine
// so callers don't fire empty patches.
export const updateCatalogPatchSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    isExcludedFromCms: z.boolean().optional(),
  })
  .refine((patch) => Object.values(patch).some((v) => v !== undefined), {
    message: 'patch must contain at least one field to update',
  });
export type UpdateCatalogPatch = z.infer<typeof updateCatalogPatchSchema>;

export const updateCatalogInput = z.object({
  id: z.uuid(),
  patch: updateCatalogPatchSchema,
});
export type UpdateCatalogInput = z.infer<typeof updateCatalogInput>;

// `catalogs.remove` — identity only.
export const removeCatalogInput = z.object({ id: z.uuid() });
export type RemoveCatalogInput = z.infer<typeof removeCatalogInput>;

// `catalogs.share` / `catalogs.unshare` — owner grants/revokes visibility to
// other organizations. Backing store: `products.catalog_share` (composite PK
// on `(catalog_id, organization_id)`).
export const shareCatalogInput = z.object({
  catalogId: z.uuid(),
  organizationIds: z.array(z.uuid()).min(1),
});
export type ShareCatalogInput = z.infer<typeof shareCatalogInput>;

export const unshareCatalogInput = shareCatalogInput;
export type UnshareCatalogInput = ShareCatalogInput;
